import {
  loadSVGFromString,
  FabricObject,
  Rect as FabricRect,
  Ellipse as FabricEllipse,
  Circle as FabricCircle,
  FabricText,
  Textbox,
  Path,
  Group,
  FabricImage,
  Polygon,
  Polyline,
} from 'fabric';
import type {
  CanvasElement,
  ShapeElement,
  TextElement,
  PathElement,
  ImageElement,
  SvgElement,
  GroupElement,
  ElementLayer,
} from '../types/index';
import { analyzeForSmartMerge, mergeCandidateToSvg } from './svgMerge';
import { preProcessSvgCss } from './svgCssPreParser';
import { extractInlineImagesFromSvg } from './svgInlineImageExtractor';
import type { FontAssignment } from '../components/editor/FontMatchDialog';
import type { SmartMergeResult } from './svgMerge';

export interface SvgDecomposedResult {
  success: boolean;
  elements?: Partial<CanvasElement>[];
  error?: string;
}

export interface SvgGroupResult {
  success: boolean;
  elementData?: Partial<SvgElement>;
  error?: string;
}

export function sanitizeSvgMarkup(markup: string): string {
  let sanitized = markup.replace(/<script[\s\S]*?<\/script>/gi, '');
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/javascript\s*:/gi, 'blocked:');
  sanitized = sanitized.replace(
    /(<(?!image)[^>]*(?:href|xlink:href)\s*=\s*["'])data:[^"']*(["'])/gi,
    '$1blocked:$2'
  );
  // Belt-and-suspenders: if any <image href="data:..."> survived the
  // extractor (e.g. extractor was skipped), drop the entire node so the
  // base64 payload never reaches canvas_state.
  sanitized = sanitized.replace(
    /<image\b[^>]*\b(?:xlink:)?href\s*=\s*["']data:[^"']*["'][^>]*\/?>/gi,
    ''
  );
  return sanitized;
}

interface SvgDimensions {
  width: number;
  height: number;
  viewBoxX: number;
  viewBoxY: number;
}

function extractSvgDimensions(svgMarkup: string): SvgDimensions {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
  const svgEl = doc.querySelector('svg');

  let width = 0;
  let height = 0;
  let viewBoxX = 0;
  let viewBoxY = 0;

  if (svgEl) {
    const viewBox = svgEl.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        viewBoxX = parts[0];
        viewBoxY = parts[1];
        width = parts[2];
        height = parts[3];
      }
    }
    if (!width || !height) {
      const wAttr = svgEl.getAttribute('width');
      const hAttr = svgEl.getAttribute('height');
      if (wAttr) width = parseFloat(wAttr) || 0;
      if (hAttr) height = parseFloat(hAttr) || 0;
    }
  }

  return { width: width || 300, height: height || 300, viewBoxX, viewBoxY };
}

function computeScaleAndOffset(
  svgDims: SvgDimensions,
  artboard: { width: number; height: number }
): { scale: number; offsetX: number; offsetY: number } {
  const maxW = artboard.width * 0.8;
  const maxH = artboard.height * 0.8;
  const ar = svgDims.width / svgDims.height;

  let scale: number;
  if (svgDims.width <= maxW && svgDims.height <= maxH) {
    scale = 1;
  } else if (ar > maxW / maxH) {
    scale = maxW / svgDims.width;
  } else {
    scale = maxH / svgDims.height;
  }

  const scaledW = svgDims.width * scale;
  const scaledH = svgDims.height * scale;
  const offsetX = (artboard.width - scaledW) / 2;
  const offsetY = (artboard.height - scaledH) / 2;

  return { scale, offsetX, offsetY };
}

interface ResolvedStroke {
  stroke: string;
  strokeWidth: number;
}

/**
 * Coherent stroke resolution for imported SVG shapes. Returns a
 * {stroke, strokeWidth} pair that is always internally consistent:
 *  - "no stroke in source" => stroke='none', strokeWidth=0
 *  - "stroke present"      => stroke=<color>, strokeWidth=max(sw*scale, 0)
 *
 * This prevents ghost strokes caused by defaulting to '#000000' or
 * non-zero widths when the source had stroke="none" or omitted the attr.
 */
function resolveStrokeFromFabric(obj: FabricObject, scale: number): ResolvedStroke {
  const raw = (obj as unknown as { stroke?: unknown }).stroke;
  const rawOpacity = (obj as unknown as { strokeOpacity?: unknown }).strokeOpacity;
  const rawWidth = (obj as unknown as { strokeWidth?: unknown }).strokeWidth;

  const hasColor =
    typeof raw === 'string' &&
    raw.length > 0 &&
    raw !== 'none' &&
    raw !== 'transparent';

  const opacity = typeof rawOpacity === 'number' ? rawOpacity : 1;
  const width = typeof rawWidth === 'number' ? rawWidth : 0;

  if (!hasColor || opacity <= 0 || width <= 0) {
    return { stroke: 'none', strokeWidth: 0 };
  }

  return {
    stroke: raw as string,
    strokeWidth: Math.max(width * scale, 0),
  };
}

function optimizePathData(pathData: string): string {
  return pathData.replace(
    /(\d+\.\d{3,})/g,
    (match) => parseFloat(match).toFixed(2)
  );
}

function optimizeSvgMarkup(markup: string): string {
  return markup.replace(
    /(\d+\.\d{3,})/g,
    (match) => parseFloat(match).toFixed(2)
  );
}

function fabricObjectToElement(
  obj: FabricObject,
  index: number,
  svgDims: SvgDimensions,
  scale: number,
  offsetX: number,
  offsetY: number,
  baseZIndex: number,
  layer: ElementLayer,
  sourceFileName: string
): Partial<CanvasElement> | null {
  // Phase Batch 2 / Bug 5 fix: normalize origin to top-left before reading
  // position and rotation. Fabric may assign originX/originY: 'center' during
  // SVG parse for certain transform configurations. The downstream CanvasElement
  // store convention is top-left (Decision [12]). Without this normalization,
  // rotated objects render with a position offset proportional to their size.
  if (obj.originX !== 'left' || obj.originY !== 'top') {
    const centerPoint = obj.getCenterPoint
      ? obj.getCenterPoint()
      : { x: obj.left ?? 0, y: obj.top ?? 0 };
    obj.originX = 'left';
    obj.originY = 'top';
    if (typeof obj.setPositionByOrigin === 'function') {
      obj.setPositionByOrigin(centerPoint, 'center', 'center');
    }
    if (typeof obj.setCoords === 'function') {
      obj.setCoords();
    }
  }

  const left = obj.left ?? 0;
  const top = obj.top ?? 0;
  const objWidth = (obj.width ?? 0) * (obj.scaleX ?? 1);
  const objHeight = (obj.height ?? 0) * (obj.scaleY ?? 1);
  const angle = obj.angle ?? 0;
  const opacity = obj.opacity ?? 1;

  const artboardX = (left - svgDims.viewBoxX) * scale + offsetX;
  const artboardY = (top - svgDims.viewBoxY) * scale + offsetY;
  const artboardW = Math.max(objWidth * scale, 1);
  const artboardH = Math.max(objHeight * scale, 1);

  const id = crypto.randomUUID();
  const zIndex = baseZIndex + index;

  const base = {
    id,
    x: artboardX,
    y: artboardY,
    width: artboardW,
    height: artboardH,
    rotation: angle,
    opacity,
    locked: false,
    visible: true,
    zIndex,
    layer,
    contentType: 'STATIC' as const,
  };

  if (obj instanceof FabricRect) {
    const fill = obj.fill;
    const rx = obj.rx ?? 0;
    const strokeCfg = resolveStrokeFromFabric(obj, scale);

    return {
      ...base,
      type: 'shape',
      name: `Rect ${index + 1}`,
      shapeType: rx > 0 ? 'rounded-rect' : 'rectangle',
      fill: typeof fill === 'string' ? fill : '#cccccc',
      fillOpacity: 1,
      stroke: strokeCfg.stroke,
      strokeWidth: strokeCfg.strokeWidth,
      borderRadius: rx * scale,
    } as Partial<ShapeElement>;
  }

  // Phase Batch 2 / Bug 3 design note:
  // FabricEllipse and FabricCircle are converted to a unified 'circle'
  // shapeType using the scaled bounding box (width/height) as the canonical
  // dimensional representation. We deliberately do NOT read obj.rx, obj.ry,
  // or obj.radius. Rationale: the downstream ShapeElement type uses
  // width/height (not explicit radii) and the bounding-box approach is
  // stable across Fabric parse paths. A circle with radius=50 and an
  // ellipse with rx=50,ry=50 are equivalent in our representation.
  // If round-trip fidelity becomes a requirement, capture obj.rx/obj.ry
  // into ShapeElement.originalRx/originalRy fields (does not exist yet).
  if (obj instanceof FabricEllipse || obj instanceof FabricCircle) {
    const fill = obj.fill;
    const strokeCfg = resolveStrokeFromFabric(obj, scale);

    return {
      ...base,
      type: 'shape',
      name: `Ellipse ${index + 1}`,
      shapeType: 'circle',
      fill: typeof fill === 'string' ? fill : '#cccccc',
      fillOpacity: 1,
      stroke: strokeCfg.stroke,
      strokeWidth: strokeCfg.strokeWidth,
      borderRadius: 0,
    } as Partial<ShapeElement>;
  }

  if (obj instanceof FabricText || obj instanceof Textbox) {
    console.log(`[SVG Font Size] Raw Fabric text object:`, {
      text: (obj.text ?? '').substring(0, 40),
      fontSize: obj.fontSize,
      fontFamily: obj.fontFamily,
      fontWeight: obj.fontWeight,
      fontStyle: obj.fontStyle,
      scaleX: obj.scaleX,
      scaleY: obj.scaleY,
      width: obj.width,
      height: obj.height,
      styles: obj.styles ? 'HAS STYLES' : 'NO STYLES',
      type: obj.type
    });

    let effectiveFontFamily = obj.fontFamily ?? 'Arial';
    let effectiveFontSize = obj.fontSize ?? 16;
    let effectiveFontWeight = obj.fontWeight ?? '400';
    let effectiveFontStyle = obj.fontStyle ?? 'normal';

    const objStyles = obj.styles as Record<string, Record<string, Record<string, unknown>>> | undefined;
    if (objStyles && typeof objStyles === 'object' && Object.keys(objStyles).length > 0) {
      const familyCounts = new Map<string, number>();
      const familySizes = new Map<string, number[]>();
      const familyWeights = new Map<string, string>();
      const familyStyles = new Map<string, string>();
      let totalStyledChars = 0;

      for (const lineKey of Object.keys(objStyles)) {
        const line = objStyles[lineKey];
        if (!line || typeof line !== 'object') continue;
        for (const charKey of Object.keys(line)) {
          const charStyle = line[charKey];
          if (!charStyle || typeof charStyle !== 'object') continue;
          totalStyledChars++;
          const ff = charStyle.fontFamily as string | undefined;
          if (ff) {
            familyCounts.set(ff, (familyCounts.get(ff) || 0) + 1);
            const fs = charStyle.fontSize as number | undefined;
            if (fs) {
              const sizes = familySizes.get(ff) || [];
              sizes.push(fs);
              familySizes.set(ff, sizes);
            }
            if (charStyle.fontWeight) familyWeights.set(ff, String(charStyle.fontWeight));
            if (charStyle.fontStyle) familyStyles.set(ff, String(charStyle.fontStyle));
          }
        }
      }

      if (familyCounts.size > 0) {
        let dominantFamily = '';
        let maxCount = 0;
        for (const [family, count] of familyCounts) {
          if (count > maxCount) {
            maxCount = count;
            dominantFamily = family;
          }
        }

        const dominantSizes = familySizes.get(dominantFamily) || [];
        const dominantSize = dominantSizes.length > 0
          ? dominantSizes.reduce((a, b) => Math.max(a, b), 0)
          : effectiveFontSize;

        const isDefaultFamily = effectiveFontFamily === 'Times New Roman' || effectiveFontFamily === 'serif' || effectiveFontFamily === 'Arial';
        const isDefaultSize = effectiveFontSize <= 16 && dominantSize > effectiveFontSize;
        const stylesDiffer = dominantFamily !== effectiveFontFamily || dominantSize !== effectiveFontSize;

        console.log(`[SVG Font Size] Multi-tspan check: obj.fontSize=${effectiveFontSize} obj.fontFamily="${effectiveFontFamily}" styles: ${totalStyledChars} chars, dominant="${dominantFamily}" size=${dominantSize}, isDefault=${isDefaultFamily}, sizeDefault=${isDefaultSize}, differs=${stylesDiffer}`);

        if (isDefaultFamily || isDefaultSize || (stylesDiffer && totalStyledChars > 0)) {
          console.log(`[SVG Font Size] Multi-tspan: using styles fontSize ${dominantSize} instead of obj.fontSize ${obj.fontSize}`);
          console.log(`[SVG Font Size] Multi-tspan: using styles fontFamily "${dominantFamily}" instead of obj.fontFamily "${obj.fontFamily}"`);
          effectiveFontFamily = dominantFamily;
          effectiveFontSize = dominantSize;
          if (familyWeights.has(dominantFamily)) effectiveFontWeight = familyWeights.get(dominantFamily)!;
          if (familyStyles.has(dominantFamily)) effectiveFontStyle = familyStyles.get(dominantFamily)!;
        }
      }
    }

    const fill = obj.fill;
    const rawFontSize = effectiveFontSize;
    const textScaleY = obj.scaleY ?? 1;
    const computedFontSize = rawFontSize * textScaleY;
    const preservedFontSize = Math.round(computedFontSize * 100) / 100;
    const textContent = obj.text ?? '';

    console.log(`[SVG Font Size] Text "${(textContent).substring(0, 30)}" — raw: ${rawFontSize}, scaleY: ${textScaleY}, preserved: ${preservedFontSize} (artboardScale ${scale} NOT applied to font)`);

    if (textScaleY && Math.abs(textScaleY - 1) > 0.01) {
      console.log(`[SVG Import] Text fontSize adjusted by scaleY: raw=${rawFontSize}, scaleY=${textScaleY.toFixed(3)}, preserved=${preservedFontSize} — "${textContent.substring(0, 30)}"`);
    }

    let name = textContent.substring(0, 30).trim();
    if (textContent.length > 30) {
      const lastSpace = name.lastIndexOf(' ');
      if (lastSpace > 15) name = name.substring(0, lastSpace);
      name += '...';
    }
    if (!name) name = `Text ${index + 1}`;

    return {
      ...base,
      type: 'text',
      layer: 'OVERLAY_LAYER',
      name,
      text: textContent,
      fontSize: preservedFontSize,
      fontFamily: effectiveFontFamily,
      fontWeight: parseInt(String(effectiveFontWeight), 10) || 400,
      fontStyle: (effectiveFontStyle === 'italic' ? 'italic' : 'normal') as 'normal' | 'italic',
      textDecoration: 'none',
      textAlign: (obj.textAlign ?? 'left') as 'left' | 'center' | 'right',
      lineHeight: obj.lineHeight ?? 1.2,
      letterSpacing: 0,
      textTransform: 'none',
      color: typeof fill === 'string' ? fill : '#000000',
    } as Partial<TextElement>;
  }

  if (obj instanceof Path) {
    const fill = obj.fill;
    const strokeCfg = resolveStrokeFromFabric(obj, scale);

    let pathData = '';
    try {
      if (obj.path && Array.isArray(obj.path)) {
        pathData = optimizePathData(
          (obj.path as unknown[][]).map((cmd) => cmd.join(' ')).join(' ')
        );
      }
    } catch {
      pathData = '';
    }

    const hasFill = typeof fill === 'string' && fill !== 'none' && fill !== 'transparent' && fill !== '';

    return {
      ...base,
      type: 'path',
      name: `Path ${index + 1}`,
      pathData,
      anchors: [],
      closed: hasFill,
      fill: hasFill ? fill : 'transparent',
      fillOpacity: hasFill ? 1 : 0,
      stroke: strokeCfg.stroke,
      strokeWidth: strokeCfg.strokeWidth,
      strokeDashStyle: 'solid',
      lineCap: 'round',
      lineJoin: 'miter',
    } as Partial<PathElement>;
  }

  if (obj instanceof Polygon || obj instanceof Polyline) {
    const fill = obj.fill;
    const strokeCfg = resolveStrokeFromFabric(obj, scale);
    const points = obj.points ?? [];

    let pathData = '';
    if (points.length > 0) {
      pathData = `M ${points[0].x} ${points[0].y}`;
      for (let i = 1; i < points.length; i++) {
        pathData += ` L ${points[i].x} ${points[i].y}`;
      }
      if (obj instanceof Polygon) pathData += ' Z';
      pathData = optimizePathData(pathData);
    }

    const hasFill = typeof fill === 'string' && fill !== 'none' && fill !== 'transparent' && fill !== '';

    return {
      ...base,
      type: 'path',
      name: `Shape ${index + 1}`,
      pathData,
      anchors: [],
      closed: obj instanceof Polygon,
      fill: hasFill ? fill : 'transparent',
      fillOpacity: hasFill ? 1 : 0,
      stroke: strokeCfg.stroke,
      strokeWidth: strokeCfg.strokeWidth,
      strokeDashStyle: 'solid',
      lineCap: 'round',
      lineJoin: 'miter',
    } as Partial<PathElement>;
  }

  if (obj instanceof FabricImage) {
    const imgEl = obj.getElement() as HTMLImageElement | null;
    const src = imgEl?.src ?? '';

    // Never embed base64 data URIs in canvas_state. If extraction upstream
    // failed to convert this to a Storage URL, drop the element entirely.
    if (src.startsWith('data:')) {
      console.warn('[SVG Parse] Dropping inline base64 image element (use Asset Manager instead).');
      return null;
    }

    return {
      ...base,
      type: 'image',
      name: `Image ${index + 1}`,
      src,
      alt: '',
      objectFit: 'cover',
    } as Partial<ImageElement>;
  }

  if (obj instanceof Group) {
    const groupSvg = optimizeSvgMarkup(obj.toSVG());
    const wrappedSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${objWidth.toFixed(2)} ${objHeight.toFixed(2)}">${groupSvg}</svg>`;

    return {
      ...base,
      type: 'svg',
      name: `Group ${index + 1}`,
      svgMarkup: wrappedSvg,
      originalWidth: objWidth,
      originalHeight: objHeight,
      preserveAspectRatio: true,
      sourceFileName,
    } as Partial<SvgElement>;
  }

  const fallbackSvg = optimizeSvgMarkup(obj.toSVG());
  const wrappedFallback = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${objWidth.toFixed(2)} ${objHeight.toFixed(2)}">${fallbackSvg}</svg>`;

  return {
    ...base,
    type: 'svg',
    name: `Element ${index + 1}`,
    svgMarkup: wrappedFallback,
    originalWidth: objWidth,
    originalHeight: objHeight,
    preserveAspectRatio: true,
    sourceFileName,
  } as Partial<SvgElement>;
}

function flattenFabricObjects(
  objects: FabricObject[],
  parentLeft = 0,
  parentTop = 0,
  parentScaleX = 1,
  parentScaleY = 1,
  parentAngle = 0,
  parentOpacity = 1
): FabricObject[] {
  const result: FabricObject[] = [];

  if (objects.length > 0) {
    console.log(`[SVG Import] flattenFabricObjects called with ${objects.length} objects, parent: left=${parentLeft.toFixed(1)}, top=${parentTop.toFixed(1)}, scaleX=${parentScaleX.toFixed(3)}, scaleY=${parentScaleY.toFixed(3)}, opacity=${parentOpacity.toFixed(2)}`);
    objects.slice(0, 3).forEach((obj, i) => {
      console.log(`[SVG Import]   obj[${i}]: type=${obj.type}, left=${obj.left?.toFixed(1)}, top=${obj.top?.toFixed(1)}, width=${obj.width?.toFixed(1)}, height=${obj.height?.toFixed(1)}, scaleX=${obj.scaleX?.toFixed(3)}, scaleY=${obj.scaleY?.toFixed(3)}, opacity=${obj.opacity?.toFixed(2)}`);
    });
  }

  for (const obj of objects) {
    if (obj instanceof Group) {
      const groupLeft = (obj.left ?? 0) * parentScaleX + parentLeft;
      const groupTop = (obj.top ?? 0) * parentScaleY + parentTop;
      const groupScaleX = (obj.scaleX ?? 1) * parentScaleX;
      const groupScaleY = (obj.scaleY ?? 1) * parentScaleY;
      const groupAngle = (obj.angle ?? 0) + parentAngle;
      const groupOpacity = (obj.opacity ?? 1) * parentOpacity;

      const children = obj.getObjects();
      const flattened = flattenFabricObjects(
        children,
        groupLeft,
        groupTop,
        groupScaleX,
        groupScaleY,
        groupAngle,
        groupOpacity
      );
      result.push(...flattened);
    } else {
      obj.left = (obj.left ?? 0) * parentScaleX + parentLeft;
      obj.top = (obj.top ?? 0) * parentScaleY + parentTop;
      obj.scaleX = (obj.scaleX ?? 1) * parentScaleX;
      obj.scaleY = (obj.scaleY ?? 1) * parentScaleY;
      obj.angle = (obj.angle ?? 0) + parentAngle;
      obj.opacity = (obj.opacity ?? 1) * parentOpacity;
      result.push(obj);
    }
  }

  return result;
}

// Hierarchy-preserving walker used by parseSvgFileDecomposed.
// Applies cumulative parent transforms to leaves (matching flattenFabricObjects)
// but keeps Fabric Group nodes in the returned tree so callers can rebuild
// the SVG's <g> structure as CanvasElement GroupElements.
type HierarchyNode =
  | { kind: 'leaf'; obj: FabricObject }
  | { kind: 'group'; obj: Group; name: string; children: HierarchyNode[] };

function groupNameFromFabric(obj: Group, fallbackIndex: number): string {
  const candidates = [
    (obj as unknown as { id?: unknown }).id,
    (obj as unknown as { label?: unknown }).label,
    (obj as unknown as { name?: unknown }).name,
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  return `Group ${fallbackIndex + 1}`;
}

function buildHierarchy(
  objects: FabricObject[],
  parentLeft = 0,
  parentTop = 0,
  parentScaleX = 1,
  parentScaleY = 1,
  parentAngle = 0,
  parentOpacity = 1,
  groupCounterRef: { n: number } = { n: 0 }
): HierarchyNode[] {
  const result: HierarchyNode[] = [];

  for (const obj of objects) {
    if (obj instanceof Group) {
      const groupLeft = (obj.left ?? 0) * parentScaleX + parentLeft;
      const groupTop = (obj.top ?? 0) * parentScaleY + parentTop;
      const groupScaleX = (obj.scaleX ?? 1) * parentScaleX;
      const groupScaleY = (obj.scaleY ?? 1) * parentScaleY;
      const groupAngle = (obj.angle ?? 0) + parentAngle;
      const groupOpacity = (obj.opacity ?? 1) * parentOpacity;

      const name = groupNameFromFabric(obj, groupCounterRef.n);
      groupCounterRef.n++;

      const children = buildHierarchy(
        obj.getObjects(),
        groupLeft,
        groupTop,
        groupScaleX,
        groupScaleY,
        groupAngle,
        groupOpacity,
        groupCounterRef
      );
      result.push({ kind: 'group', obj, name, children });
    } else {
      obj.left = (obj.left ?? 0) * parentScaleX + parentLeft;
      obj.top = (obj.top ?? 0) * parentScaleY + parentTop;
      obj.scaleX = (obj.scaleX ?? 1) * parentScaleX;
      obj.scaleY = (obj.scaleY ?? 1) * parentScaleY;
      obj.angle = (obj.angle ?? 0) + parentAngle;
      obj.opacity = (obj.opacity ?? 1) * parentOpacity;
      result.push({ kind: 'leaf', obj });
    }
  }

  return result;
}

export async function parseSvgFileDecomposed(
  file: File,
  artboardDimensions: { width: number; height: number },
  baseZIndex: number,
  layer: ElementLayer = 'BACKGROUND_LAYER'
): Promise<SvgDecomposedResult> {
  try {
    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      console.error('[SVG Parse] Not an SVG file. Name:', file.name, 'Type:', file.type);
      return { success: false, error: 'File is not an SVG. Please select an .svg file.' };
    }

    const rawMarkup = await file.text();

    if (!rawMarkup.includes('<svg') || !rawMarkup.includes('</svg>')) {
      console.error('[SVG Parse] Missing <svg> or </svg> tags. File:', file.name, 'Content preview:', rawMarkup.substring(0, 200));
      return { success: false, error: 'File does not contain valid SVG markup.' };
    }

    const extractedMarkup = await extractInlineImagesFromSvg(rawMarkup, file.name);
    const svgMarkup = sanitizeSvgMarkup(extractedMarkup);
    const svgDims = extractSvgDimensions(svgMarkup);
    console.log(`[SVG Import] Pre-processing CSS for decomposed parse. Input length: ${svgMarkup.length.toLocaleString()}`);
    const processedSvg = preProcessSvgCss(svgMarkup);

    const firstTextEl = processedSvg.match(/<text[^>]*>/g);
    if (firstTextEl) {
      firstTextEl.slice(0, 3).forEach((t, i) => {
        console.log(`[SVG Font Size] Processed SVG <text> #${i}: ${t.substring(0, 300)}`);
      });
    }
    const firstTspanEl = processedSvg.match(/<tspan[^>]*>/g);
    if (firstTspanEl) {
      firstTspanEl.slice(0, 5).forEach((t, i) => {
        console.log(`[SVG Font Size] Processed SVG <tspan> #${i}: ${t.substring(0, 300)}`);
      });
    }

    const parseResult = await loadSVGFromString(processedSvg);
    const objects = parseResult.objects.filter((obj): obj is FabricObject => obj !== null);

    if (objects.length === 0) {
      console.error('[SVG Parse] Fabric.js returned 0 renderable objects. File:', file.name, 'Processed SVG length:', processedSvg.length);
      return { success: false, error: 'SVG contains no renderable elements.' };
    }

    const tree = buildHierarchy(objects);
    const { scale, offsetX, offsetY } = computeScaleAndOffset(svgDims, artboardDimensions);

    const elements: Partial<CanvasElement>[] = [];
    let zCounter = 0;
    let groupCount = 0;
    let leafConversionFailures = 0;
    let leafConversionAttempts = 0;

    const emit = (nodes: HierarchyNode[]): string[] => {
      const childIds: string[] = [];
      for (const node of nodes) {
        if (node.kind === 'leaf') {
          leafConversionAttempts++;
          const el = fabricObjectToElement(
            node.obj,
            zCounter,
            svgDims,
            scale,
            offsetX,
            offsetY,
            baseZIndex,
            layer,
            file.name
          );
          if (el && el.id) {
            elements.push(el);
            childIds.push(el.id);
            zCounter++;
          } else {
            leafConversionFailures++;
          }
        } else {
          const nestedChildIds = emit(node.children);
          if (nestedChildIds.length === 0) continue;

          const childElements = elements.filter((e) => nestedChildIds.includes(e.id as string));
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          for (const c of childElements) {
            const cx = (c.x ?? 0);
            const cy = (c.y ?? 0);
            const cw = (c.width ?? 0);
            const ch = (c.height ?? 0);
            if (cx < minX) minX = cx;
            if (cy < minY) minY = cy;
            if (cx + cw > maxX) maxX = cx + cw;
            if (cy + ch > maxY) maxY = cy + ch;
          }
          if (!isFinite(minX)) { minX = 0; minY = 0; maxX = 0; maxY = 0; }

          const groupEl: Partial<GroupElement> = {
            id: crypto.randomUUID(),
            type: 'group',
            name: node.name,
            x: minX,
            y: minY,
            width: Math.max(maxX - minX, 1),
            height: Math.max(maxY - minY, 1),
            rotation: 0,
            opacity: 1,
            visible: true,
            locked: false,
            zIndex: baseZIndex + zCounter,
            layer,
            contentType: 'STATIC',
            childIds: nestedChildIds,
          };
          elements.push(groupEl as CanvasElement);
          childIds.push(groupEl.id as string);
          zCounter++;
          groupCount++;
        }
      }
      return childIds;
    };

    emit(tree);

    if (groupCount > 0) {
      console.log(`[SVG Import] Preserved ${groupCount} <g> group(s) as editable GroupElements`);
    }

    if (elements.length === 0) {
      console.error('[SVG Parse] All elements failed conversion. File:', file.name, 'Leaf attempts:', leafConversionAttempts, 'Failures:', leafConversionFailures);
      return { success: false, error: `All ${leafConversionAttempts} SVG elements failed to convert. The file may use unsupported SVG features.` };
    }

    const textElements = elements.filter(el => el.type === 'text');
    if (textElements.length > 0) {
      console.log('[SVG Font Size] Final text element font sizes:',
        textElements.map(el => ({
          text: ((el as Partial<TextElement>).text ?? '').substring(0, 30),
          fontSize: (el as Partial<TextElement>).fontSize,
          fontFamily: (el as Partial<TextElement>).fontFamily
        }))
      );
    }

    return { success: true, elements };
  } catch (err) {
    console.error('SVG decomposed import failed:', err);
    return {
      success: false,
      error: `Failed to import SVG: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

export async function parseSvgFile(
  file: File,
  artboardDimensions: { width: number; height: number },
  zIndex: number
): Promise<SvgGroupResult> {
  try {
    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      return { success: false, error: 'File is not an SVG. Please select an .svg file.' };
    }

    const rawMarkup = await file.text();

    if (!rawMarkup.includes('<svg') || !rawMarkup.includes('</svg>')) {
      return { success: false, error: 'File does not contain valid SVG markup.' };
    }

    const extractedMarkup = await extractInlineImagesFromSvg(rawMarkup, file.name);
    let svgMarkup = sanitizeSvgMarkup(extractedMarkup);

    // Phase Batch 2 / Bug 1 fix: route grouped import through CSS pre-processor
    // for consistency with parseSvgFileDecomposed and parseSvgFileSmartMerge.
    // preProcessSvgCss is a no-op when the SVG has no <style> blocks.
    svgMarkup = preProcessSvgCss(svgMarkup);

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const svgElement = svgDoc.querySelector('svg');

    if (!svgElement) {
      return { success: false, error: 'Could not parse SVG element from file.' };
    }

    let originalWidth = 0;
    let originalHeight = 0;

    const viewBox = svgElement.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        originalWidth = parts[2];
        originalHeight = parts[3];
      }
    }

    if (!originalWidth || !originalHeight) {
      const widthAttr = svgElement.getAttribute('width');
      const heightAttr = svgElement.getAttribute('height');
      if (widthAttr && heightAttr) {
        originalWidth = parseFloat(widthAttr) || 0;
        originalHeight = parseFloat(heightAttr) || 0;
      }
    }

    originalWidth = originalWidth || 300;
    originalHeight = originalHeight || 300;

    const aspectRatio = originalWidth / originalHeight;
    const maxWidth = artboardDimensions.width * 0.8;
    const maxHeight = artboardDimensions.height * 0.8;

    let targetWidth: number;
    let targetHeight: number;

    if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
      targetWidth = originalWidth;
      targetHeight = originalHeight;
    } else {
      if (aspectRatio > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = maxWidth / aspectRatio;
      } else {
        targetHeight = maxHeight;
        targetWidth = maxHeight * aspectRatio;
      }
    }

    const x = (artboardDimensions.width - targetWidth) / 2;
    const y = (artboardDimensions.height - targetHeight) / 2;

    const name = file.name
      .replace(/\.svg$/i, '')
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .substring(0, 40);

    return {
      success: true,
      elementData: {
        id: crypto.randomUUID(),
        type: 'svg',
        name,
        svgMarkup,
        x,
        y,
        width: targetWidth,
        height: targetHeight,
        originalWidth,
        originalHeight,
        preserveAspectRatio: true,
        sourceFileName: file.name,
        rotation: 0,
        opacity: 1,
        locked: false,
        visible: true,
        zIndex,
        layer: 'BACKGROUND_LAYER',
        contentType: 'STATIC',
      },
    };
  } catch (err) {
    console.error('SVG group import failed:', err);
    return {
      success: false,
      error: `Failed to import SVG: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

export async function analyzeSvgForSmartMerge(file: File): Promise<SmartMergeResult | null> {
  try {
    const rawMarkup = await file.text();
    if (!rawMarkup.includes('<svg') || !rawMarkup.includes('</svg>')) return null;

    const svgMarkup = sanitizeSvgMarkup(rawMarkup);
    console.log(`[SVG Import] Pre-processing CSS for smart merge analysis. Input length: ${svgMarkup.length.toLocaleString()}`);
    const processedSvg = preProcessSvgCss(svgMarkup);
    const parseResult = await loadSVGFromString(processedSvg);
    const objects = parseResult.objects.filter((obj): obj is FabricObject => obj !== null);
    if (objects.length === 0) return null;

    const flatObjects = flattenFabricObjects(objects);
    return analyzeForSmartMerge(flatObjects);
  } catch (err) {
    console.error('SVG smart merge analysis failed:', err);
    return null;
  }
}

export interface SvgSmartMergeResult {
  success: boolean;
  elements?: Partial<CanvasElement>[];
  mergeStats?: { originalCount: number; mergedCount: number; groups: { name: string; count: number }[] };
  error?: string;
}

export async function parseSvgFileSmartMerge(
  file: File,
  artboardDimensions: { width: number; height: number },
  baseZIndex: number,
  layer: ElementLayer = 'BACKGROUND_LAYER'
): Promise<SvgSmartMergeResult> {
  try {
    if (!file.name.toLowerCase().endsWith('.svg') && file.type !== 'image/svg+xml') {
      return { success: false, error: 'File is not an SVG. Please select an .svg file.' };
    }

    const rawMarkup = await file.text();
    if (!rawMarkup.includes('<svg') || !rawMarkup.includes('</svg>')) {
      return { success: false, error: 'File does not contain valid SVG markup.' };
    }

    const extractedMarkup = await extractInlineImagesFromSvg(rawMarkup, file.name);
    const svgMarkup = sanitizeSvgMarkup(extractedMarkup);
    const svgDims = extractSvgDimensions(svgMarkup);
    console.log(`[SVG Import] Pre-processing CSS for smart merge parse. Input length: ${svgMarkup.length.toLocaleString()}`);
    const processedSvg = preProcessSvgCss(svgMarkup);

    const parseResult = await loadSVGFromString(processedSvg);
    const objects = parseResult.objects.filter((obj): obj is FabricObject => obj !== null);

    if (objects.length === 0) {
      return { success: false, error: 'SVG contains no renderable elements.' };
    }

    const flatObjects = flattenFabricObjects(objects);
    const { scale, offsetX, offsetY } = computeScaleAndOffset(svgDims, artboardDimensions);
    const analysis = analyzeForSmartMerge(flatObjects);

    const elements: Partial<CanvasElement>[] = [];
    let currentIndex = 0;

    for (const group of analysis.mergedGroups) {
      const merged = mergeCandidateToSvg(group);

      const artboardX = (merged.x - svgDims.viewBoxX) * scale + offsetX;
      const artboardY = (merged.y - svgDims.viewBoxY) * scale + offsetY;
      const artboardW = Math.max(merged.width * scale, 1);
      const artboardH = Math.max(merged.height * scale, 1);

      elements.push({
        id: crypto.randomUUID(),
        type: 'svg',
        name: group.suggestedName,
        x: artboardX,
        y: artboardY,
        width: artboardW,
        height: artboardH,
        rotation: 0,
        opacity: 1,
        locked: false,
        visible: true,
        zIndex: baseZIndex + currentIndex,
        layer,
        contentType: 'STATIC',
        svgMarkup: merged.svgMarkup,
        originalWidth: merged.width,
        originalHeight: merged.height,
        preserveAspectRatio: true,
        sourceFileName: file.name,
      } as Partial<SvgElement>);
      currentIndex++;
    }

    for (let i = 0; i < analysis.individuals.length; i++) {
      const el = fabricObjectToElement(
        analysis.individuals[i],
        currentIndex + i,
        svgDims,
        scale,
        offsetX,
        offsetY,
        baseZIndex,
        layer,
        file.name
      );
      if (el) elements.push(el);
    }

    if (elements.length === 0) {
      return { success: false, error: 'Could not convert any SVG elements.' };
    }

    return {
      success: true,
      elements,
      mergeStats: {
        originalCount: analysis.originalCount,
        mergedCount: analysis.mergedCount,
        groups: analysis.mergedGroups.map((g) => ({ name: g.suggestedName, count: g.count })),
      },
    };
  } catch (err) {
    console.error('SVG smart merge import failed:', err);
    return {
      success: false,
      error: `Failed to import SVG: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

export function applyFontAssignments(
  elements: Partial<CanvasElement>[],
  assignments: FontAssignment[]
): Partial<CanvasElement>[] {
  if (!assignments || assignments.length === 0) return elements;

  const textCount = elements.filter(e => e.type === 'text').length;
  console.log(`[SVG Font] applyFontAssignments called with ${textCount} text elements and ${assignments.length} assignments`);
  assignments.forEach(a => {
    console.log(`[SVG Font]   Assignment: "${a.originalCss}" → "${a.assignedFamily}" (weight: ${a.assignedWeight ?? 'auto'})`);
  });

  const assignmentMap = new Map<string, FontAssignment>();
  for (const a of assignments) {
    if (!a.skipped && a.assignedFamily) {
      assignmentMap.set(a.originalCss, a);
    }
  }

  if (assignmentMap.size === 0) return elements;

  return elements.map(element => {
    if (element.type === 'text') {
      const textEl = element as Partial<TextElement>;
      let matched = false;
      for (const [originalCss, assignment] of assignmentMap.entries()) {
        const currentFont = (textEl.fontFamily || '').trim();

        if (originalCss === currentFont && assignment.assignedFamily) {
          const originalFamily = textEl.fontFamily;
          console.log(`[SVG Font]   Matched text "${(textEl.text ?? '').substring(0, 30)}" — fontFamily "${textEl.fontFamily}" → "${assignment.assignedFamily}" (exact match)`);
          matched = true;
          const updated = {
            ...textEl,
            fontFamily: assignment.assignedFamily,
            fontWeight: assignment.assignedWeight || textEl.fontWeight || 400,
            fontStyle: (assignment.assignedStyle || textEl.fontStyle || 'normal') as 'normal' | 'italic',
            fontVariantId: assignment.assignedVariantId || textEl.fontVariantId,
            svgFontSource: originalCss,
          };
          console.log(`[SVG Font] WROTE fontFamily on element: "${updated.fontFamily}" (was: "${originalFamily}")`);
          return updated;
        }

        const assignmentCandidates = originalCss.split(',').map((v: string) => v.trim().replace(/^['"]|['"]$/g, ''));
        const elementCandidates = currentFont.split(',').map((v: string) => v.trim().replace(/^['"]|['"]$/g, ''));

        const isMatch = assignmentCandidates.some((assignCandidate: string) => {
          return elementCandidates.some((elCandidate: string) => {
            if (assignCandidate === elCandidate) return true;
            const normAssign = assignCandidate.toLowerCase().replace(/[\s\-_']/g, '');
            const normEl = elCandidate.toLowerCase().replace(/[\s\-_']/g, '');
            return normAssign === normEl;
          });
        });

        if (isMatch && assignment.assignedFamily) {
          const originalFamily = textEl.fontFamily;
          console.log(`[SVG Font]   Matched text "${(textEl.text ?? '').substring(0, 30)}" — fontFamily "${textEl.fontFamily}" → "${assignment.assignedFamily}" (candidate match)`);
          matched = true;
          const updated = {
            ...textEl,
            fontFamily: assignment.assignedFamily,
            fontWeight: assignment.assignedWeight || textEl.fontWeight || 400,
            fontStyle: (assignment.assignedStyle || textEl.fontStyle || 'normal') as 'normal' | 'italic',
            fontVariantId: assignment.assignedVariantId || textEl.fontVariantId,
            svgFontSource: originalCss,
          };
          console.log(`[SVG Font] WROTE fontFamily on element: "${updated.fontFamily}" (was: "${originalFamily}")`);
          return updated;
        }
      }
      if (!matched) {
        console.log(`[SVG Font]   No match for text "${(textEl.text ?? '').substring(0, 30)}" — fontFamily "${textEl.fontFamily}" not found in any assignment.originalCss`);
      }
    }

    if (element.type === 'svg' && (element as Partial<SvgElement>).svgMarkup) {
      let markup = (element as Partial<SvgElement>).svgMarkup as string;
      for (const [originalCss, assignment] of assignmentMap.entries()) {
        if (!assignment.assignedFamily) continue;
        const escaped = originalCss.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escaped, 'g');
        markup = markup.replace(regex, assignment.assignedFamily);
      }
      return { ...element, svgMarkup: markup } as Partial<SvgElement>;
    }

    return element;
  });
}
